/**
 * Operational errors, reduced to a closed set of categories.
 *
 * A job's `last_error` and a reconciliation cursor's `last_error` are raw
 * strings from Discord, Bedrock, or PostgreSQL. Any of them can quote a prompt,
 * a member's message, a model's output, or a connection URL, so none of them may
 * reach the browser. What an operator actually needs from that field is which
 * *kind* of failure it was, and that is a closed set.
 *
 * The classifier only ever emits a category from the list below plus the length
 * of the text it did not show, so an operator can tell "there was a detailed
 * error" from "the field was empty" and go read the container logs. Adding a
 * category is a code change with a test, which is the point: nothing can widen
 * the output by supplying a cleverer error string.
 */

export const OPERATIONAL_ERROR_CATEGORIES = [
  'budget_exhausted',
  'discord_permission',
  'discord_rate_limited',
  'discord_unavailable',
  'discord_not_found',
  'model_throttled',
  'model_unavailable',
  'database_error',
  'timeout',
  'lease_lost',
  'unclassified',
] as const;

export type OperationalErrorCategory = (typeof OPERATIONAL_ERROR_CATEGORIES)[number];

export const OPERATIONAL_ERROR_LABELS: Readonly<Record<OperationalErrorCategory, string>> = {
  budget_exhausted: 'Monthly budget reached',
  discord_permission: 'Discord permission denied',
  discord_rate_limited: 'Discord rate limited',
  discord_unavailable: 'Discord unavailable',
  discord_not_found: 'Discord resource missing',
  model_throttled: 'Model throttled',
  model_unavailable: 'Model unavailable',
  database_error: 'Database error',
  timeout: 'Timed out',
  lease_lost: 'Worker lease lost',
  unclassified: 'Unclassified',
};

/**
 * Patterns are matched against a lowercased copy and are all fixed substrings of
 * error *shapes*, never of content. Order matters: the first match wins, so the
 * more specific patterns come first.
 */
const PATTERNS: readonly (readonly [OperationalErrorCategory, readonly string[]])[] = [
  ['budget_exhausted', ['budget', 'hard limit', 'monthly limit', 'reservation']],
  ['lease_lost', ['lost_lease', 'lease']],
  ['timeout', ['timeout', 'timed out', 'etimedout', 'aborted']],
  ['discord_permission', ['missing access', 'missing permissions', '50001', '50013']],
  ['discord_rate_limited', ['rate limit', 'ratelimit', '429']],
  ['discord_not_found', ['unknown channel', 'unknown message', 'unknown guild', '10003', '10008']],
  ['discord_unavailable', ['discordapierror', 'discord.com', 'gateway', '503 service']],
  ['model_throttled', ['throttlingexception', 'too many requests', 'toomanyrequests']],
  [
    'model_unavailable',
    ['bedrock', 'modelerror', 'validationexception', 'serviceunavailable', 'accessdenied'],
  ],
  ['database_error', ['econnrefused', 'postgres', 'relation ', 'deadlock', 'statement timeout']],
];

export interface ClassifiedError {
  category: OperationalErrorCategory;
  /** Characters withheld. Zero means the source field was empty or absent. */
  withheldChars: number;
}

export function classifyOperationalError(raw: string | null | undefined): ClassifiedError {
  if (raw === null || raw === undefined || raw.length === 0) {
    return { category: 'unclassified', withheldChars: 0 };
  }
  const haystack = raw.toLowerCase();
  for (const [category, needles] of PATTERNS) {
    for (const needle of needles) {
      if (haystack.includes(needle)) return { category, withheldChars: raw.length };
    }
  }
  return { category: 'unclassified', withheldChars: raw.length };
}

/**
 * Every failure the dashboard API itself can report, as a closed set.
 *
 * Same reasoning as above, one layer up: a caught exception's message can carry
 * a database connection string, a stack frame, or an environment value, so the
 * browser is told what kind of failure happened and nothing more.
 *
 * This file is bundled into the client, and Vite's sourcemap carries its
 * comments verbatim — so the *names* of server-only variables are spelled out
 * nowhere in it. They are not secrets, but a browser artifact has no reason to
 * enumerate them, and React Doctor's `artifact-env-leak` rule is right to say
 * so.
 */
export const API_ERROR_CODES = [
  'unauthorized',
  'forbidden',
  'invalid_request',
  'invalid_csrf',
  'not_found',
  'rate_limited',
  'payload_too_large',
  'bot_unreachable',
  'bot_rejected',
  'guild_not_found',
  'member_not_found',
  'channel_not_authorized',
  'timeout',
  'database_error',
  'internal_error',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

export const API_ERROR_LABELS: Readonly<Record<ApiErrorCode, string>> = {
  unauthorized: 'Your session has expired. Sign in again.',
  forbidden: 'That action is not permitted.',
  invalid_request: 'The request was not valid.',
  invalid_csrf: 'The request could not be verified. Reload the page and try again.',
  not_found: 'Not found.',
  rate_limited: 'Too many attempts. Wait and try again.',
  payload_too_large: 'That request was too large.',
  bot_unreachable: 'The bot process could not be reached.',
  bot_rejected: 'The bot process refused the request.',
  guild_not_found: 'That server is not in the bot’s cache right now.',
  member_not_found: 'That member could not be resolved in the server.',
  channel_not_authorized: 'That channel is outside the selected member’s readable scope.',
  timeout: 'The request took too long and was stopped.',
  database_error: 'The database could not be reached.',
  internal_error: 'Something went wrong. Check the container logs.',
};
